import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { DataRow, DataTable } from "@/components/ui/data-table"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { TaskList } from "@/components/tasks/TaskList"
import type { TaskRecord } from "@/components/tasks/types"
import type { PageProps } from "@/types/inertia"

type ClientOption = {
  readonly id: number
  readonly slug: string
  readonly display_name: string
  readonly color_hex: string
}

type ContactDetail = {
  readonly id: number
  readonly name: string
  readonly email: string
}

type AccountMini = {
  readonly id: number
  readonly name: string
  readonly domain: string
}

type OpportunityDetail = {
  readonly id: number
  readonly name: string
  readonly coarse_stage: string
  readonly lifecycle_stage_id: string
  readonly status: string
  readonly service_line: string
  readonly value_cents: number
  readonly currency: string
  readonly source: string
  readonly lost_reason: string | null
  readonly next_step_note: string | null
  readonly won_at: string | null
  readonly lost_at: string | null
  readonly parked_at: string | null
  readonly stage_changed_at: string | null
  readonly created_at: string
  readonly client: ClientOption
  readonly account: AccountMini | null
  readonly primary_contact: ContactDetail | null
}

type ReplySummary = {
  readonly id: number
  readonly subject: string
  readonly received_at: string | null
  readonly status: string
  readonly lead_name: string
  readonly client: ClientOption | null
  readonly detached: boolean
}

type AuditEntry = {
  readonly id: number
  readonly action: string
  readonly payload: Record<string, unknown>
  readonly created_at: string
  readonly user_id: number | null
}

type StageMeta = {
  readonly key: string
  readonly label: string
  readonly lifecycle_ids: readonly string[]
}

type OpportunityShowProps = {
  readonly opportunity: OpportunityDetail
  readonly replies: readonly ReplySummary[]
  readonly tasks: readonly TaskRecord[]
  readonly audit_trail: readonly AuditEntry[]
  readonly stage_meta: readonly StageMeta[]
  readonly clients: readonly ClientOption[]
}

export default function OpportunityShow({ opportunity, replies, tasks, audit_trail, stage_meta }: OpportunityShowProps) {
  const { props } = usePage<PageProps<OpportunityShowProps>>()
  const description = `CRM opportunity ${opportunity.name} — stage, value, replies, tasks, and audit trail.`
  const currentMeta = stage_meta.find((m) => m.key === opportunity.coarse_stage)
  const lifecycleIds = currentMeta?.lifecycle_ids ?? []
  const won = opportunity.won_at !== null

  const [editingName, setEditingName] = React.useState(false)
  const [nameValue, setNameValue] = React.useState(opportunity.name)
  const [nextStepNote, setNextStepNote] = React.useState(opportunity.next_step_note ?? "")

  const saveName = () => {
    if (nameValue.trim() && nameValue.trim() !== opportunity.name) {
      router.patch(`/crm/opportunities/${opportunity.id}`, { name: nameValue.trim() }, { preserveScroll: true })
    }
    setEditingName(false)
  }

  const saveNextStepNote = () => {
    if (nextStepNote.trim() !== (opportunity.next_step_note ?? "")) {
      router.patch(`/crm/opportunities/${opportunity.id}`, { next_step_note: nextStepNote.trim() }, { preserveScroll: true })
    }
  }

  const updateField = (field: string, value: string | number) => {
    router.patch(`/crm/opportunities/${opportunity.id}`, { [field]: value }, { preserveScroll: true })
  }

  const moveStatus = (status: string) => {
    router.post(`/crm/opportunities/${opportunity.id}/status`, { status }, { preserveScroll: true })
  }

  const setStage = (stage: string) => {
    router.post(`/crm/opportunities/${opportunity.id}/stage`, { stage }, { preserveScroll: true })
  }

  const formattedValue = opportunity.value_cents > 0
    ? new Intl.NumberFormat("en-US", { style: "currency", currency: opportunity.currency.toUpperCase() }).format(opportunity.value_cents / 100)
    : null

  return (
    <>
      <Head title={opportunity.name}>
        <meta name="description" content={description} />
        <meta property="og:title" content={opportunity.name} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell wide>
        <div className="flex flex-col gap-6">
          <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              {editingName ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={nameValue}
                    onChange={(e) => setNameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveName()
                      if (e.key === "Escape") { setNameValue(opportunity.name); setEditingName(false) }
                    }}
                    className="w-64"
                    autoFocus
                  />
                  <Button type="button" size="sm" onClick={saveName}>Save</Button>
                  <Button type="button" variant="secondary" size="sm" onClick={() => { setNameValue(opportunity.name); setEditingName(false) }}>Cancel</Button>
                </div>
              ) : (
                <h1
                  className="cursor-pointer hover:text-accent"
                  onClick={() => setEditingName(true)}
                  title="Click to edit"
                >
                  {opportunity.name}
                </h1>
              )}
              <div className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 rounded-full border border-hairline"
                  style={{ backgroundColor: opportunity.client.color_hex }}
                />
                <span className="text-sm text-ink-muted">{opportunity.client.display_name}</span>
              </div>
              {won && <Badge tone="accent">Won</Badge>}
            </div>
            <div className="flex gap-2">
              <Button asChild variant="secondary">
                <Link href="/crm/opportunities">Back to board</Link>
              </Button>
            </div>
          </header>

          {props.flash.notice && (
            <div className="rounded-md border border-accent bg-accent-faded px-4 py-3 text-sm text-accent-display">
              {props.flash.notice}
            </div>
          )}

          <section className="rounded-lg border border-hairline bg-page px-6">
            <DataTable>
              <DataRow title="Stage">
                <div className="flex items-center gap-2">
                  <Badge tone="neutral">{currentMeta?.label ?? opportunity.coarse_stage}</Badge>
                  <Select
                    value=""
                    onChange={(e) => e.target.value && setStage(e.target.value)}
                    className="w-auto min-w-40"
                  >
                    <option value="" disabled>Move stage</option>
                    {stage_meta.filter((m) => m.key !== opportunity.coarse_stage).map((m) => (
                      <option key={m.key} value={m.key}>{m.label}</option>
                    ))}
                  </Select>
                </div>
              </DataRow>
              <DataRow title="Lifecycle">
                <Select
                  value={opportunity.lifecycle_stage_id}
                  onChange={(e) => updateField("lifecycle_stage_id", e.target.value)}
                  className="w-auto min-w-24 font-mono"
                >
                  {lifecycleIds.map((id) => (
                    <option key={id} value={id}>{id}</option>
                  ))}
                  {!lifecycleIds.includes(opportunity.lifecycle_stage_id) && (
                    <option value={opportunity.lifecycle_stage_id}>{opportunity.lifecycle_stage_id}</option>
                  )}
                </Select>
              </DataRow>
              <DataRow title="Status">
                <div className="flex items-center gap-2">
                  <Badge tone="muted">{opportunity.status}</Badge>
                  {opportunity.status !== "parked" && (
                    <Button type="button" variant="secondary" size="sm" onClick={() => moveStatus("parked")}>
                      Park
                    </Button>
                  )}
                  {opportunity.status !== "active" && (
                    <Button type="button" variant="secondary" size="sm" onClick={() => moveStatus("active")}>
                      Reactivate
                    </Button>
                  )}
                  {opportunity.status !== "lost" && (
                    <Button type="button" variant="secondary" size="sm" onClick={() => moveStatus("lost")}>
                      Mark Lost
                    </Button>
                  )}
                  {opportunity.status !== "dead" && (
                    <Button type="button" variant="secondary" size="sm" onClick={() => moveStatus("dead")}>
                      Mark Dead
                    </Button>
                  )}
                </div>
              </DataRow>
              <DataRow title="Service line">
                <Select
                  value={opportunity.service_line}
                  onChange={(e) => updateField("service_line", e.target.value)}
                  className="w-auto min-w-36"
                >
                  <option value="leadgen">Leadgen</option>
                  <option value="automation">Automation</option>
                </Select>
              </DataRow>
              <DataRow title="Value">
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={(opportunity.value_cents / 100).toString()}
                    onChange={(e) => updateField("value_cents", Math.round(parseFloat(e.target.value || "0") * 100))}
                    className="w-36"
                  />
                  <span className="text-sm text-ink-muted uppercase">{opportunity.currency}</span>
                </div>
              </DataRow>
              <DataRow title="Currency">
                <span className="text-sm uppercase">{opportunity.currency}</span>
              </DataRow>
              {opportunity.account && (
                <DataRow title="Account">
                  <Link href={`/crm/accounts/${opportunity.account.id}`} className="no-underline hover:underline">
                    {opportunity.account.name || opportunity.account.domain}
                  </Link>
                </DataRow>
              )}
              {opportunity.primary_contact && (
                <DataRow title="Primary contact">
                  <Link href={`/contacts/${opportunity.primary_contact.id}`} className="no-underline hover:underline">
                    {opportunity.primary_contact.name}
                  </Link>
                </DataRow>
              )}
              {opportunity.lost_reason && (
                <DataRow title="Lost reason">{opportunity.lost_reason}</DataRow>
              )}
              {formattedValue && (
                <DataRow title="Deal value (formatted)">{formattedValue}</DataRow>
              )}
              {opportunity.won_at && (
                <DataRow title="Won at">{formatDate(opportunity.won_at)}</DataRow>
              )}
              <DataRow title="Created">{formatDate(opportunity.created_at)}</DataRow>
            </DataTable>
          </section>

          <section className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <label htmlFor="next_step_note" className="text-xs font-medium uppercase tracking-wide text-ink-muted">
                Next step note
              </label>
            </div>
            <div className="flex gap-2">
              <Input
                id="next_step_note"
                value={nextStepNote}
                placeholder="e.g. follow up after their board meeting"
                onChange={(e) => setNextStepNote(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    saveNextStepNote()
                  }
                }}
              />
              <Button type="button" variant="secondary" size="sm" onClick={saveNextStepNote}>
                Save
              </Button>
            </div>
          </section>

          <section className="flex flex-col gap-4">
            <h2>Replies</h2>
            {replies.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-hairline bg-page">
                <div className="divide-y divide-hairline">
                  {replies.map((reply) => (
                    <Link
                      key={reply.id}
                      href={`/inbox?selected=${reply.id}`}
                      className="grid gap-2 px-4 py-4 no-underline hover:bg-surface sm:grid-cols-[1fr_auto] sm:items-center sm:gap-4"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-ink-display">
                          {reply.subject || reply.lead_name}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-ink-muted">
                          <span>{reply.lead_name}</span>
                          {reply.client && (
                            <span className="inline-flex items-center gap-1.5">
                              <span
                                className="h-2 w-2 rounded-full border border-hairline"
                                style={{ backgroundColor: reply.client.color_hex }}
                              />
                              {reply.client.display_name}
                            </span>
                          )}
                          <span>{formatDateTime(reply.received_at)}</span>
                        </div>
                      </div>
                      <div className="flex gap-2 sm:justify-end">
                        <Badge tone="muted">{reply.status}</Badge>
                        {reply.detached && <Badge tone="muted">detached</Badge>}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-ink-muted">No replies linked to this opportunity yet.</p>
            )}
          </section>

          <section className="flex flex-col gap-4">
            <h2>Tasks</h2>
            <TaskList tasks={tasks} emptyText="No tasks for this opportunity yet." />
          </section>

          <section className="flex flex-col gap-4">
            <h2>Audit trail</h2>
            {audit_trail.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-hairline bg-page">
                <div className="divide-y divide-hairline">
                  {audit_trail.map((entry) => (
                    <div key={entry.id} className="px-4 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <Badge tone="muted">{entry.action}</Badge>
                          <span className="text-sm text-ink-body">
                            {auditSummary(entry)}
                          </span>
                        </div>
                        <time className="text-xs text-ink-muted shrink-0">{formatDateTime(entry.created_at)}</time>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-ink-muted">No audit events yet.</p>
            )}
          </section>
        </div>
      </AppShell>
    </>
  )
}

function auditSummary(entry: AuditEntry): string {
  switch (entry.action) {
    case "crm.stage_change": {
      const payload = entry.payload as { before?: { coarse_stage?: string }, after?: { coarse_stage?: string } }
      const before = payload?.before?.coarse_stage ?? "?"
      const after = payload?.after?.coarse_stage ?? "?"
      return `Stage changed: ${before} → ${after}`
    }
    case "crm.status_change": {
      const payload = entry.payload as { before?: { status?: string }, after?: { status?: string } }
      const before = payload?.before?.status ?? "?"
      const after = payload?.after?.status ?? "?"
      return `Status changed: ${before} → ${after}`
    }
    default:
      return entry.action
  }
}

function formatDate(value: string) {
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

function formatDateTime(value: string | null) {
  if (!value) return "Unknown"
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value))
}
